import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Col, Container, Row, Card, CardBody, TabContent, TabPane } from 'reactstrap';
import {
    DashboardPageHeader,
    ResponsiveTabs,
} from '../../Components/Common/DashboardKit';
import CncTab from './components/CncTab';
import ShuklaTab from './components/ShuklaTab';
import WindingTab from './components/WindingTab';
import ProductionTab from './components/ProductionTab';
import QualityTab from './components/QualityTab';
import { useTrailLeaf } from "../../Components/Common/DashboardKit/PageHeaderTrailContext";

/**
 * Display Boards landing page — live shop-floor status across CNC,
 * Shukla, Winding, Production-Flex, and Quality.
 *
 * Pass 5 UI update (2026):
 *  - Tab strip migrated from inline <Nav>/<NavItem>/<NavLink> to the
 *    shared ResponsiveTabs from the DashboardKit. Same five tabs, same
 *    icons, same active behavior. The "render only the active tab"
 *    lazy-mount pattern is preserved exactly — each TabPane still
 *    guards its child with `{activeTab === tab.id && <TabComponent />}`,
 *    so an inactive tab does not fetch.
 *  - Removed `classnames` import (the shell handles active-state class
 *    application internally now).
 *  - Removed Nav/NavItem/NavLink imports from reactstrap.
 *  - The TABS array, the activeTab state, and the toggle handler are
 *    unchanged.
 */
// Go-live parity (2026-09-24): `slug` is the ?board= value, so each shop-floor
// TV can open its own board directly (legacy picked the board from the PC name).
const TABS = [
    { id: '1', slug: 'cnc',        label: 'CNC',               icon: 'ri-settings-3-line',   component: CncTab },
    { id: '2', slug: 'shukla',     label: 'Shukla',            icon: 'ri-building-2-line',   component: ShuklaTab },
    { id: '3', slug: 'winding',    label: 'Winding',           icon: 'ri-loop-left-line',    component: WindingTab },
    { id: '4', slug: 'production', label: 'Production - Flex', icon: 'ri-hammer-line',       component: ProductionTab },
    { id: '5', slug: 'quality',    label: 'Quality',           icon: 'ri-shield-check-line', component: QualityTab },
];

// Tabs reshaped for ResponsiveTabs (key/label/icon). Memoized at module
// scope since the source list is static — no need to rebuild per render.
const KIT_TABS = TABS.map((t) => ({ key: t.id, label: t.label, icon: t.icon }));

const DisplayBoards = () => {
    document.title = 'Display Board | SSW Technologies Dashboard';

    // Go-live parity (2026-09-24): ?board=cnc|shukla|winding|production|quality
    // picks the tab (and survives a reload); ?kiosk=1 is the chrome-less TV view.
    const [searchParams, setSearchParams] = useSearchParams();
    const kiosk = searchParams.get('kiosk') === '1';
    const [activeTab, setActiveTab] = useState(() => {
        const slug = (searchParams.get('board') || '').toLowerCase();
        return (TABS.find((t) => t.slug === slug) || TABS[0]).id;
    });
    const activeSlug = (TABS.find((t) => t.id === activeTab) || TABS[0]).slug;
    // Owner 2026-10-12: the board shows in the topbar trail.
    useTrailLeaf([{ label: (TABS.find((t) => t.id === activeTab) || TABS[0]).label }], 1);

    const toggle = (tab) => {
        if (activeTab !== tab) {
            setActiveTab(tab);
            const next = new URLSearchParams(searchParams);
            next.set('board', (TABS.find((t) => t.id === tab) || TABS[0]).slug);
            setSearchParams(next);   // Owner 2026-10-12: Back returns to the previous board
        }
    };

    return (
        <React.Fragment>
            <div className={kiosk ? 'p-2' : 'page-content'}>
                <Container fluid>
                    {kiosk ? (
                        <div className="d-flex justify-content-end mb-2">
                            <Link to={`/display-boards?board=${activeSlug}`} className="btn btn-sm btn-light">
                                <i className="ri-fullscreen-exit-line me-1"></i>Exit TV view
                            </Link>
                        </div>
                    ) : (
                    <>
                    <DashboardPageHeader
                        icon="ri-tv-2-line"
                        eyebrow="Shop Floor"
                        title="Display Boards"
                        subtitle="Live status across CNC, Shukla, Winding, Production-Flex, and Quality."
                        tone="teal"
                        breadcrumb={[
                            { label: "Dashboards", to: "/" },
                            { label: "Display Boards" },
                        ]}
                    />
                    <div className="d-flex justify-content-end mb-2">
                        <Link
                            to={`/display-boards?board=${activeSlug}&kiosk=1`}
                            className="btn btn-sm btn-soft-success"
                            title="Open this board without the menus, for a shop-floor TV. Bookmark the address on the TV."
                        >
                            <i className="ri-tv-2-line me-1"></i>TV view
                        </Link>
                    </div>
                    </>
                    )}
                    <Row>
                        <Col xxl={12}>
                            <Card>
                                <CardBody>
                                    <ResponsiveTabs
                                        tabs={KIT_TABS}
                                        activeKey={activeTab}
                                        onChange={toggle}
                                        variant="underline"
                                        color="success"
                                        justified
                                    />

                                    <TabContent activeTab={activeTab} className="text-muted">
                                        {TABS.map((tab) => {
                                            const TabComponent = tab.component;
                                            return (
                                                <TabPane tabId={tab.id} key={tab.id}>
                                                    {activeTab === tab.id && <TabComponent />}
                                                </TabPane>
                                            );
                                        })}
                                    </TabContent>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        </React.Fragment>
    );
};

export default DisplayBoards;
